import { Link } from 'react-router-dom-v5-compat';
import CreatableSelect from 'react-select/creatable';
import {
  formButtonMediumSemibold,
  formLabelSmallSemibold,
} from 'src/components/pages/CoiFormManagement/sharedFormManagement.styles';
import { textStyleCss } from 'src/theme/atlas';
import styled from 'styled-components';

export * from './formManagementTable.styles';

export const PageContainer = styled.div`
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.surface.default};
  padding: 24px 48px;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  flex: 1;
  min-height: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow-x: hidden;
  overflow-y: auto;
  overflow-anchor: none;

  button[data-testid='create-form-button'] {
    border: none;
  }
`;

export const ContentContainer = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 24px;
  width: 100%;
  min-width: 0;
  min-height: 0;
  box-sizing: border-box;
  overflow-anchor: none;
`;

export const HeaderSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

export const FormProductTabs = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 8px;
  width: 100%;
  border-bottom: 1px solid
    ${(props) => props.theme.atlas.semantic.color.ui.divider.default};
  box-sizing: border-box;
`;

export const FormProductTab = styled.button<{ $active: boolean }>`
  position: relative;
  box-sizing: border-box;
  height: 30px;
  padding: 2px 12px;
  border-radius: ${(props) => props.theme.atlas.semantic.radius.md}
    ${(props) => props.theme.atlas.semantic.radius.md} 0 0;
  border: 0;
  background: transparent;
  cursor: pointer;
  color: ${(props) =>
    props.theme.atlas.semantic.color.action.secondary.onSecondary};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  font-weight: 600;
  line-height: 24px;
  letter-spacing: 0.2px;
  text-align: center;
  white-space: nowrap;

  &::after {
    content: '';
    position: absolute;
    left: 0;
    right: 0;
    bottom: -1px;
    height: 2px;
    border-radius: 2px 2px 0 0;
    background-color: ${(props) =>
      props.$active
        ? props.theme.atlas.semantic.color.action.secondaryInverse
            .selectionIndicatorTabs
        : 'transparent'};
  }

  &:hover:not(:disabled) {
    background-color: ${(props) =>
      props.theme.atlas.semantic.color.action.secondary.hoverFill};
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: -2px;
  }
`;

export const EmptyStateWrapper = styled.div`
  flex: 1;
  width: 100%;
  min-height: 420px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 16px;
  padding: 48px 0;
  box-sizing: border-box;
`;

export const CreateFormEmptyStateButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: ${(props) => props.theme.atlas.semantic.radius.sm};
  border: none;
  cursor: pointer;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.action.primary.default};
  color: ${(props) => props.theme.atlas.semantic.color.surface.default};
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};

  &:hover {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.action.primary.hover};
    outline-offset: 2px;
  }
`;

export const FormManagementCategoryConfigurationLink = styled(Link)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  box-sizing: border-box;
  padding: 8px 16px;
  border-radius: ${(props) => props.theme.atlas.semantic.radius.sm};
  border: none;
  cursor: pointer;
  background-color: ${(props) =>
    props.theme.atlas.semantic.color.action.primary.default};
  color: ${(props) => props.theme.atlas.semantic.color.surface.default};
  text-decoration: none;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)};

  &:hover {
    filter: brightness(0.95);
  }

  &:focus-visible {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.action.primary.hover};
    outline-offset: 2px;
  }
`;

export const ErrorStateWrapper = styled.div`
  width: 100%;
  display: flex;
  justify-content: center;
  padding: 48px 0;
`;

export const ActiveCategoriesWrapper = styled.div`
  width: 100%;
  position: relative;
`;

export const FormsSection = styled.div`
  width: 100%;
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

export const FormsFilterRecoveryRow = styled.div`
  display: flex;
  align-items: center;
  width: 100%;
`;

export const FormsSectionHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  width: 100%;

  h3 {
    margin: 0;
    ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.headingH3)}
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }
`;

export const FormsTabsWrapper = styled.div`
  display: flex;
  align-items: center;
`;

export const FormsTableToolbar = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  flex-wrap: wrap;
  width: 100%;
  min-width: 0;
  min-height: 64px;
`;

export const FormsSearchInputWrapper = styled.div`
  flex: 1;
  min-width: 0;
  display: flex;
  align-items: center;

  input {
    margin-top: 0;
  }
`;

export const FormsToolbarField = styled.div<{
  $width?: string;
  $minWidth?: string;
}>`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  flex: none;
  ${(props) => props.$width && `width: ${props.$width};`}
  ${(props) => props.$minWidth && `min-width: ${props.$minWidth};`}
`;

export const FormsToolbarLabel = styled.label`
  ${formLabelSmallSemibold}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};
`;

export const FormsSearchSelect = styled(CreatableSelect)`
  width: 100%;

  .css-1s2u09g-control,
  .css-1pahdxg-control,
  [class*='-control'] {
    min-height: 40px;
    border-radius: ${(props) => props.theme.atlas.semantic.radius.md};
    transition: outline 0.2s ease;
    flex-wrap: nowrap;
    flex-direction: row-reverse;
  }

  [class*='-control']:hover {
    border-color: ${(props) =>
      props.theme.atlas.semantic.color.action.primary.default};
  }

  &:focus-within [class*='-control'] {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
    border-color: ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
    box-shadow: none;
  }

  [class*='-valueContainer'] {
    overflow: hidden;
    flex-wrap: nowrap;
    min-width: 0;
  }

  [class*='-indicatorsContainer'] {
    flex-shrink: 0;
  }

  [class*='-multiValue'] {
    max-width: calc(100% - 8px);
    min-width: 0;
    flex-shrink: 1;
  }

  [class*='-multiValueLabel'] {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

export const FormsToolbarRight = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 16px;
  min-width: 0;
  flex: none;
`;

export const FormsResetFiltersButtonText = styled.span`
  ${formButtonMediumSemibold}
  color: inherit;
`;

export const FormsResetFiltersButton = styled.button`
  display: inline-flex;
  align-items: center;
  align-self: flex-end;
  gap: 8px;
  min-height: 40px;
  padding: 0;
  border: none;
  background: transparent;
  box-shadow: none;
  cursor: pointer;
  ${({ theme }) => textStyleCss(theme.atlas.typography.semantic.bodySmall)}
  color: ${(props) => props.theme.atlas.semantic.color.type.default};

  &,
  &:hover,
  &:focus,
  & * {
    cursor: pointer;
  }

  &:hover {
    color: ${(props) => props.theme.atlas.semantic.color.type.default};
  }

  &:focus {
    outline: 2px solid
      ${(props) => props.theme.atlas.semantic.color.ui.focusRing};
    outline-offset: 2px;
    border-radius: 4px;
  }

  &:disabled,
  &:disabled * {
    cursor: not-allowed;
    color: ${(props) => props.theme.atlas.semantic.color.type.disabled};
  }
`;

export const FormsTabsRow = styled.div`
  display: flex;
  align-items: center;
`;

export const NoCategoriesBannerWrapper = styled.div`
  width: 100%;
`;

export const StatusFilterWrapper = styled.div`
  > div > button {
    background: ${(props) =>
      props.theme.isVaultVariant
        ? props.theme.newPalette.neutral.N0
        : props.theme.atlas.semantic.color.surface.default} !important;
    border: 1px solid
      ${(props) =>
        props.theme.isVaultVariant
          ? props.theme.newPalette.neutral.N300
          : props.theme.atlas.core.color.gray[300]} !important;
  }
`;
